import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from './api';
import { captureAttribution, trackAttributionEvent } from './attribution';

// Hook to capture and store referral codes from URL
export const useReferralTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    const refSource = params.get('utm_source');
    const captured = captureAttribution(location.search);
    if (captured) {
      const productMatch = location.pathname.match(/^\/products\/([^/]+)/);
      trackAttributionEvent(productMatch ? 'product_view' : 'landing', productMatch ? productMatch[1] : null);
      if (!refCode && captured.kind === 'utm') window.history.replaceState({}, '', window.location.pathname);
    }
    
    if (refCode) {
      // Store referral code
      localStorage.setItem('referral_code', refCode);
      localStorage.setItem('referral_timestamp', Date.now().toString());
      if (refSource) localStorage.setItem('referral_source', refSource);
      
      // Track the click
      trackReferralClick(refCode, refSource);
      
      // Clean URL without losing the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);
};

// Track referral click
const trackReferralClick = async (refCode, source) => {
  try {
    await api.post(`/affiliate/track-click?referral_code=${refCode}${source ? `&source=${encodeURIComponent(source)}` : ''}`);
  } catch (error) {
    // Silent fail - don't block user experience for tracking
  }
};

// Get stored referral code (valid for 30 days)
export const getStoredReferral = () => {
  const code = localStorage.getItem('referral_code');
  const timestamp = localStorage.getItem('referral_timestamp');
  
  if (!code || !timestamp) return null;
  
  // Check if referral is still valid (30 days)
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  if (Date.now() - parseInt(timestamp) > thirtyDays) {
    clearReferral();
    return null;
  }
  
  return code;
};

// Clear referral after successful order
export const clearReferral = () => {
  localStorage.removeItem('referral_code');
  localStorage.removeItem('referral_timestamp');
};

// Calculate discount
export const calculateReferralDiscount = (subtotal) => {
  const refCode = getStoredReferral();
  if (!refCode) return { discount: 0, code: null };
  
  const discount = subtotal * 0.10; // 10% discount
  return { discount, code: refCode };
};

// Track conversion when order is placed
export const trackReferralConversion = async (orderId, orderTotal) => {
  const refCode = getStoredReferral();
  if (!refCode) return;
  
  try {
    await api.post('/affiliate/track-conversion', null, {
      params: {
        referral_code: refCode,
        order_id: orderId,
        order_total: orderTotal,
        source: localStorage.getItem('referral_source') || undefined
      }
    });
    
    // Clear referral after successful conversion
    clearReferral();
  } catch (error) {
    // Silent fail - conversion tracking is non-critical
  }
};
